import Component from "@glimmer/component";
import { on } from "@ember/modifier";
import { t } from "ember-intl";
import { task as concurrencyTask } from "ember-concurrency";
import task from "ember-concurrency/helpers/task";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntro from "glesys-controlpanel/components/page-content/service-intro";
import { cached } from "@glimmer/tracking";
import { sshKeyList, sshKeyRemove } from "glesys-controlpanel/data/ssh-key/builders";
import { Request } from "@warp-drive/ember";
import GButtonIcon from "glesys-controlpanel/components/g-button/icon";
import Copyable from "glesys-controlpanel/components/copyable";
import IconsPlus from "glesys-controlpanel/components/icons/plus";
import Skeleton from "glesys-controlpanel/components/skeleton";
import RouteError from "glesys-controlpanel/components/route-error";
import EmptyState from "glesys-controlpanel/components/empty-state";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";
import { and, not } from "ember-truth-helpers";
import { service } from "@ember/service";
import Entity from "glesys-controlpanel/components/entity";
import EntityField from "glesys-controlpanel/components/entity/fields/field";
import EntityList from "glesys-controlpanel/components/entity-list";
import { array } from "@ember/helper";
import IconsSidebarCompute from "glesys-controlpanel/components/icons/sidebar/compute";

export default class SshKeysOverview extends Component {
  @service store;

  get project() {
    return this.args.model.project;
  }

  @cached
  get sshKeyListRequest() {
    return sshKeyList(this.project.id);
  }

  deleteSshKeyTask = concurrencyTask(async (sshKey) => {
    await this.store.request(sshKeyRemove(sshKey));
  });

  <template>
    <Request @query={{this.sshKeyListRequest}}>
      <:loading>
        <Skeleton />
      </:loading>
      <:error as |error|>
        <RouteError @error={{error}} />
      </:error>
      <:content as |result|>
        <PageContent>
          <PageContentServiceIntro @title={{t "ssh-keys.page-title"}}>
            {{#if (and (canManage) result.data)}}
              <GButtonIcon
                data-test-page-title-add
                @color="blue"
                @display="default"
                {{on "click" (modal "modals/ssh-key-add" project=this.project)}}
              >
                <IconsPlus class="w-4 -ml-1 mr-1" />
                {{t "button.add"}}
              </GButtonIcon>
            {{/if}}
          </PageContentServiceIntro>

          <EntityList @items={{result.data}}>
            <:default as |sshKey|>
              <Entity @title={{sshKey.description}} @hideActions={{not (canManage)}}>
                <:fields>
                  <EntityField data-test-ssh-key-type @label={{t "label.type"}}>
                    {{sshKey.algorithm}}
                  </EntityField>
                </:fields>
                <:actions>
                  <DropdownDestructiveItem
                    data-test-ssh-key-delete
                    {{on
                      "click"
                      (modal
                        "modals/organism-delete"
                        entity=sshKey.description
                        task=(task this.deleteSshKeyTask sshKey)
                        entityKey=sshKey.description
                      )
                    }}
                  >
                    {{t "button.delete"}}
                  </DropdownDestructiveItem>
                </:actions>
                <:details>
                  <Copyable
                    class="text-sm font-mono break-all p-4 rounded-sm border border-gray-200 bg-gray-50"
                    data-test-ssh-key-data
                  >
                    {{sshKey.data}}
                  </Copyable>
                </:details>
              </Entity>
            </:default>
            <:empty>
              <EmptyState
                @icon={{IconsSidebarCompute}}
                @title={{t "ssh-keys.empty-state.title"}}
                @description={{t "ssh-keys.empty-state.description"}}
                @points={{array
                  (t "ssh-keys.empty-state.usp-list.passwordless")
                  (t "ssh-keys.empty-state.usp-list.pick-on-create")
                  (t "ssh-keys.empty-state.usp-list.available-in-project")
                }}
                @learnMoreUrl="https://docs.glesys.com/products/compute/guides-for-server-management/create-public-ssh-keys"
                @learnMoreAriaLabel={{t "ssh-keys.empty-state.learn-more-aria-label"}}
                @createLabel={{t "ssh-keys.empty-state.button"}}
                @createColor="blue"
                @onCreate={{if (canManage) (modal "modals/ssh-key-add" project=this.project)}}
              />
            </:empty>
          </EntityList>

        </PageContent>
      </:content>
    </Request>
  </template>
}
